import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'dva'

import X from '../../assets/svg/Feather/X'
import ghAnalytics from '../../lib/analytics'
import { USER_ROLES } from '../../lib/constants'
import { getIsRoleUpgradeable, getIsStaffMember } from '../../lib/utils'
import styles from './MapsUpgradePrompt.module.scss'

export const MAPS_UPGRADE_PROMPT_STORAGE_KEY = 'mapsMainUpgradePromptDismissed'
export const MAPS_UPGRADE_PROMPT_DISMISS_DAYS = 60

/**
 * Bottom pill CTA for Insider+ (maps). Dismiss hides for {@link MAPS_UPGRADE_PROMPT_DISMISS_DAYS} days.
 * Presentational + eligibility when used via default connected export.
 */
export function MapsUpgradePromptView({
  upgradeHref,
  message,
  visible,
  onDismiss,
  onUpgradeClick,
}) {
  if (!visible) return null

  return (
    <div className={styles.wrapper} role="region" aria-label="Insider Plus upgrade">
      <button type="button" className={styles.dismiss} onClick={onDismiss} aria-label="Dismiss upgrade prompt">
        <X width={18} color="currentColor" />
      </button>
      <span className={styles.message}>{message}</span>
      <a
        className={styles.upgradeLink}
        href={upgradeHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onUpgradeClick}
      >
        Upgrade
      </a>
    </div>
  )
}

MapsUpgradePromptView.propTypes = {
  upgradeHref: PropTypes.string.isRequired,
  message: PropTypes.string,
  visible: PropTypes.bool,
  onDismiss: PropTypes.func,
  onUpgradeClick: PropTypes.func,
}

MapsUpgradePromptView.defaultProps = {
  message: 'Want personalized e-scouting help?',
  visible: false,
  onDismiss: () => {},
  onUpgradeClick: () => {},
}

function readDismissStillActive(storageKey, dismissDays) {
  const raw = localStorage.getItem(storageKey)
  if (!raw) return false
  try {
    const { timestamp } = JSON.parse(raw)
    const dismissedAt = new Date(timestamp)
    const daysSince = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24)
    if (daysSince < dismissDays) return true
  } catch {
    localStorage.removeItem(storageKey)
  }
  return false
}

function MapsUpgradePrompt(props) {
  const { cmsBaseUrl, userProfile, mapReady, storageKey, dismissDays, upgradeHref: upgradeHrefProp } = props
  const [isVisible, setIsVisible] = useState(false)

  const cmsRoot = cmsBaseUrl || 'https://www.gohunt.com'
  const upgradeHref = upgradeHrefProp || `${cmsRoot}/learn/insider-plus`

  const isEligible = useMemo(() => {
    const roles = userProfile?.roles
    if (!roles?.length) return false
    const userRole = roles[0] || USER_ROLES.guest
    const normalized = userRole ? String(userRole).toLowerCase() : USER_ROLES.guest
    const canUpgrade = getIsRoleUpgradeable(USER_ROLES.insider_plus, normalized)
    const staff = getIsStaffMember(normalized)
    return canUpgrade && !staff
  }, [userProfile?.roles])

  useEffect(() => {
    if (!mapReady || !isEligible) {
      setIsVisible(false)
      return
    }
    if (readDismissStillActive(storageKey, dismissDays)) {
      setIsVisible(false)
      return
    }
    setIsVisible(true)
  }, [mapReady, isEligible, userProfile?.roles, storageKey, dismissDays])

  const handleDismiss = () => {
    setIsVisible(false)
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        timestamp: new Date().toISOString(),
      })
    )
  }

  const handleUpgradeClick = () => {
    ghAnalytics.logMapsMainUpgradeCta()
  }

  return (
    <MapsUpgradePromptView
      upgradeHref={upgradeHref}
      visible={isVisible}
      onDismiss={handleDismiss}
      onUpgradeClick={handleUpgradeClick}
    />
  )
}

MapsUpgradePrompt.propTypes = {
  cmsBaseUrl: PropTypes.string,
  userProfile: PropTypes.object,
  mapReady: PropTypes.bool,
  storageKey: PropTypes.string,
  dismissDays: PropTypes.number,
  upgradeHref: PropTypes.string,
}

MapsUpgradePrompt.defaultProps = {
  cmsBaseUrl: '',
  userProfile: {},
  mapReady: false,
  storageKey: MAPS_UPGRADE_PROMPT_STORAGE_KEY,
  dismissDays: MAPS_UPGRADE_PROMPT_DISMISS_DAYS,
  upgradeHref: null,
}

export default connect(model => ({
  cmsBaseUrl: model.app.config?.REACT_APP_GOHUNT_CMS_URL || '',
  userProfile: model.user.profile,
}))(MapsUpgradePrompt)
